import { useApp } from '../common/AppProvider';
import { translations } from '../translations/translations';
import { stringReplace } from '../common/utils';
import { Warning } from '@phosphor-icons/react';
import { Notification as TaskNotification } from './types';
import { useNavigate } from 'react-router-dom';
import { AutomateError, markNotificationsAsRead } from '../api';
import { ExButton } from '@boomi/exosphere';

import './task-notification-bar.css';

interface Props {
    taskId: string;
}

const TaskNotificationBar = ({ taskId }: Props) => {
    const { language, notifications, setNotifications, setIsLoading, alertError } = useApp();
    const navigate = useNavigate();

    const sortNotificationByDate = (taskNotifications: TaskNotification[]) =>
        taskNotifications.sort((a, b) => {
            if (a.createdAt < b.createdAt) {
                return 1;
            } else if (a.createdAt > b.createdAt) {
                return -1;
            }

            return 0;
        });

    const getTaskNotification = (taskNotifications: TaskNotification[]) => {
        if (taskNotifications.length === 0) {
            return null;
        }

        return sortNotificationByDate(taskNotifications)[0];
    };

    const getMoreErrorsMessage = (notificationsNum: number) => {
        const extraNotificationsNeeded = notificationsNum - 1;

        if (extraNotificationsNeeded <= 0) {
            return '';
        } else if (extraNotificationsNeeded === 1) {
            return translations[language].USER_NOTIFICATIONS_one_more_message;
        }

        return stringReplace(
            translations[language].USER_NOTIFICATIONS_two_plus_more_message,
            extraNotificationsNeeded.toString(),
        );
    };

    const readSingleMessage = async (notification: TaskNotification) => {
        setIsLoading(true);

        await markNotificationsAsRead([notification.id])
            .then(() => {
                notifications.forEach((element, index) => {
                    if (element.id === notification.id) {
                        notifications[index].read = true;
                    }
                });
                setNotifications([...notifications]);
            })
            .catch((e: AutomateError) =>
                alertError({
                    message: translations[language].ERROR_reading_single_notification,
                    traceId: e.json?.traceId,
                }),
            )
            .finally(() => setIsLoading(false));
    };

    const taskNotifications = notifications.filter((n) => n.taskId === taskId && !n.read);
    const notification = getTaskNotification(taskNotifications);
    const notificationMessage = notification?.message;
    const moreErrorsMessage = getMoreErrorsMessage(taskNotifications.length);

    return notificationMessage ? (
        <div className="task-notification-bar">
            <button
                className="task-notification-btn"
                onClick={() => {
                    if (notification) readSingleMessage(notification);
                }}
                title={notificationMessage ?? ''}
            >
                {notificationMessage ? (
                    <Warning className="task-notification-icon" weight="fill" />
                ) : null}
                <div className="task-notification-message">{notificationMessage}</div>
            </button>
            {moreErrorsMessage !== '' ? (
                <ExButton
                    className="task-notification-more"
                    flavor="risky"
                    size="small"
                    type="secondary"
                    onClick={(e: Event) => {
                        e.stopPropagation();
                        navigate(`../notifications/?taskId=${taskId}`);
                    }}
                >
                    {moreErrorsMessage}
                </ExButton>
            ) : null}
        </div>
    ) : null;
};

export default TaskNotificationBar;
